import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import {
  UtensilsCrossed, Plus, Loader2, Pencil, Trash2, Sparkles, X, Save,
} from 'lucide-react';
import { toast } from 'sonner';
import { CategorySelect, VariationsEditor, ImageUploader } from '../components/MenuFormControls';
import { DietaryTagPicker, DietaryTagIcons } from '../components/DietaryTags';
import EditableCategoryChip from '../components/EditableCategoryChip';
import { formatCurrency } from '../lib/currency';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const blankItem = () => ({
  group: '',
  category: 'Mains',
  name: '',
  description: '',
  photo_url: '',
  price: 0,
  is_special: false,
  sort_order: 0,
  variations: [],
  dietary_tags: [],
});

const MenuAdminPage = () => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [categoryOrders, setCategoryOrders] = useState({}); // { name: sort_order }
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | item
  const [draft, setDraft] = useState(blankItem());
  const [saving, setSaving] = useState(false);

  const featureEnabled = user?.features?.menus === true;
  // Existing category strings across ALL items (for the CategorySelect dropdown).
  const existingCategories = [...new Set(items.map((i) => i.category).filter(Boolean))];

  // Two-level chip filter state — top-level "group" (e.g. Food / Bar) and
  // second-level "category" (e.g. Mains / Curries). Data model: every item
  // has both fields; `group` defaults to "Menu" for items created before the
  // hierarchy shipped (backfilled by a startup migration on the backend).
  const [activeGroup, setActiveGroup] = useState('All');
  const [activeCategory, setActiveCategory] = useState('All');

  // Group chips are computed from ALL items (unfiltered) so the top row
  // always shows every bucket the venue uses. Items with no group set
  // are surfaced under "Uncategorized" so the admin can see + fix them
  // instead of them silently disappearing.
  const groupChips = useMemo(() => {
    const groups = Array.from(new Set(items.map((i) => i.group).filter(Boolean))).sort();
    const hasUncategorized = items.some((i) => !i.group);
    return ['All', ...groups, ...(hasUncategorized ? ['Uncategorized'] : [])];
  }, [items]);
  const groupCounts = useMemo(() => {
    const counts = { All: items.length };
    for (const it of items) {
      const g = it.group || 'Uncategorized';
      counts[g] = (counts[g] || 0) + 1;
    }
    return counts;
  }, [items]);

  // Category chips are scoped to the currently-selected group — filtering
  // by group first means the sub-category chip row only shows chips that
  // exist within that bucket (no empty "Mains" chip under the "Bar" group).
  const itemsInActiveGroup = useMemo(() => {
    if (activeGroup === 'All') return items;
    if (activeGroup === 'Uncategorized') return items.filter((i) => !i.group);
    return items.filter((i) => i.group === activeGroup);
  }, [items, activeGroup]);
  const categoryCounts = useMemo(() => {
    const counts = { All: itemsInActiveGroup.length };
    for (const it of itemsInActiveGroup) {
      const c = it.category || 'Uncategorized';
      counts[c] = (counts[c] || 0) + 1;
    }
    return counts;
  }, [itemsInActiveGroup]);
  const categoryChips = useMemo(() => {
    const cats = Array.from(new Set(itemsInActiveGroup.map((i) => i.category || 'Uncategorized')));
    // Sort by explicit sort_order ASC, then alphabetically (case-insensitive).
    // Categories without an entry default to 0 — so setting a positive value
    // pushes a chip earlier (Milk Options can precede Cakes even though "C" < "M").
    cats.sort((a, b) => {
      const ao = categoryOrders[a] || 0;
      const bo = categoryOrders[b] || 0;
      if (ao !== bo) return ao - bo;
      return a.localeCompare(b, undefined, { sensitivity: 'base' });
    });
    return ['All', ...cats];
  }, [itemsInActiveGroup, categoryOrders]);

  // Reset the sub-category chip when the parent group changes so guests
  // don't see a "no items" state from a stale sub-selection.
  useEffect(() => { setActiveCategory('All'); }, [activeGroup]);

  const visibleItems = useMemo(() => {
    let out = itemsInActiveGroup;
    if (activeCategory !== 'All') {
      out = out.filter((i) => (i.category || 'Uncategorized') === activeCategory);
    }
    return out;
  }, [itemsInActiveGroup, activeCategory]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, catsRes] = await Promise.all([
        axios.get(`${API}/menu/items`),
        axios.get(`${API}/menu/categories`).catch(() => ({ data: { categories: [] } })),
      ]);
      setItems(itemsRes.data || []);
      const orderMap = {};
      (catsRes.data?.categories || []).forEach((c) => {
        if (c?.name) orderMap[c.name] = Number(c.sort_order) || 0;
      });
      setCategoryOrders(orderMap);
    } catch (err) {
      // Likely 401 — leave items empty.
      console.warn('[MenuAdmin] fetch items failed:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setDraft(blankItem()); setEditing('new'); };
  const openEdit = (item) => {
    setDraft({
      group: item.group || '',
      category: item.category || 'Menu',
      name: item.name || '',
      description: item.description || '',
      photo_url: item.photo_url || '',
      price: item.price || 0,
      is_special: !!item.is_special,
      sort_order: item.sort_order || 0,
      variations: Array.isArray(item.variations) ? item.variations : [],
      dietary_tags: Array.isArray(item.dietary_tags) ? item.dietary_tags : [],
    });
    setEditing(item);
  };
  const closeEdit = () => { setEditing(null); setDraft(blankItem()); };

  const save = async () => {
    if (!draft.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (editing === 'new') {
        await axios.post(`${API}/menu/items`, draft);
        toast.success('Item added');
      } else {
        await axios.put(`${API}/menu/items/${editing.id}`, draft);
        toast.success('Item updated');
      }
      await load();
      closeEdit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.name}"?`)) return;
    try {
      await axios.delete(`${API}/menu/items/${item.id}`);
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      toast.success('Deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  // Bulk group rename / delete — top-level bucket operations. Host-scoped.
  const renameGroup = async (oldName, newName) => {
    try {
      const res = await axios.put(`${API}/menu/groups`, { old: oldName, new: newName });
      toast.success(`Renamed "${oldName}" → "${res.data.new}" (${res.data.updated} item${res.data.updated === 1 ? '' : 's'})`);
      setActiveGroup((cur) => (cur === oldName ? res.data.new : cur));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rename failed');
      throw e;
    }
  };
  const deleteGroup = async (name) => {
    try {
      const res = await axios.delete(`${API}/menu/groups`, { data: { group: name } });
      toast.success(`Deleted "${name}" + ${res.data.deleted} item${res.data.deleted === 1 ? '' : 's'}`);
      setActiveGroup((cur) => (cur === name ? 'All' : cur));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
      throw e;
    }
  };

  // Bulk category rename / delete — uses host endpoints (scoped to logged-in user).
  const renameCategory = async (oldName, newName) => {
    try {
      const res = await axios.put(`${API}/menu/categories`, { old: oldName, new: newName });
      toast.success(`Renamed "${oldName}" → "${res.data.new}" (${res.data.updated} item${res.data.updated === 1 ? '' : 's'})`);
      setActiveCategory((cur) => (cur === oldName ? res.data.new : cur));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rename failed');
      throw e;
    }
  };
  const deleteCategory = async (name) => {
    try {
      const res = await axios.delete(`${API}/menu/categories`, { data: { category: name } });
      toast.success(`Deleted "${name}" + ${res.data.deleted} item${res.data.deleted === 1 ? '' : 's'}`);
      setActiveCategory((cur) => (cur === name ? 'All' : cur));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
      throw e;
    }
  };

  // Persist a category's display order. `label` may be the RENAMED value
  // when rename + reorder are saved in the same edit action.
  const setCategoryOrder = async (nextOrder, label) => {
    try {
      await axios.put(`${API}/menu/categories/order`, {
        category: label,
        sort_order: Number(nextOrder) || 0,
      });
      // Optimistic — refresh from server so chip order + badge update together.
      setCategoryOrders((prev) => ({ ...prev, [label]: Number(nextOrder) || 0 }));
      toast.success(`"${label}" order set to ${Number(nextOrder) || 0}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Order save failed');
      throw e;
    }
  };

  const LayoutWrapper = user?.role === 'superadmin' ? AdminLayout : Layout;

  if (!featureEnabled) {
    return (
      <LayoutWrapper>
        <div className="p-4 lg:p-8 max-w-3xl mx-auto" data-testid="menu-feature-disabled">
          <div className="flex flex-col items-center justify-center text-center py-20 px-6 bg-[#0a0a0a] border border-white/10 rounded-3xl transform-gpu">
            <div className="w-20 h-20 bg-[#00FFFF]/10 rounded-full flex items-center justify-center mb-5">
              <UtensilsCrossed className="w-10 h-10 text-[#00FFFF]" />
            </div>
            <h2 className="text-xl lg:text-2xl font-extrabold text-white mb-2 tracking-wider font-['Outfit']">
              MENU FEATURE LOCKED
            </h2>
            <p className="text-gray-400 text-sm max-w-sm">
              The Menu feature is not enabled for your account. Ask your admin to turn it on.
            </p>
          </div>
        </div>
      </LayoutWrapper>
    );
  }

  return (
    <LayoutWrapper>
      <div className="p-4 lg:p-8 pb-32 max-w-5xl mx-auto" data-testid="menu-admin-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <UtensilsCrossed className="w-6 h-6 text-[#00FFFF]" />
            <div>
              <h1 className="text-2xl lg:text-3xl font-extrabold text-white tracking-tight font-['Outfit']">Menu</h1>
              <p className="text-gray-400 text-xs lg:text-sm">{items.length} item{items.length === 1 ? '' : 's'} · visible to guests when session is live</p>
            </div>
          </div>
          <Button
            onClick={openNew}
            className="bg-[#00FFFF] hover:bg-[#00d4d4] text-black rounded-full px-5 text-xs font-bold tracking-wider"
            data-testid="menu-add-item"
          >
            <Plus className="w-4 h-4 mr-1.5" /> ADD ITEM
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-[#00FFFF] animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 bg-white/[0.03] border border-white/10 rounded-3xl">
            <UtensilsCrossed className="w-12 h-12 text-gray-700 mx-auto mb-3" />
            <p className="text-gray-400 text-sm">No items yet. Click <span className="text-[#00FFFF] font-semibold">ADD ITEM</span> to start.</p>
          </div>
        ) : (
          <>
            {/* Group chip row (top-level: Food / Bar / etc.) */}
            <div className="flex gap-1.5 overflow-x-auto pb-2 -mx-1 px-1 mb-2 items-center" data-testid="menu-group-chips">
              <span className="text-[10px] uppercase tracking-[0.3em] text-gray-600 font-['JetBrains_Mono'] pr-1.5 flex-shrink-0">Category</span>
              {groupChips.map((g) => (
                <EditableCategoryChip
                  key={`group-${g}`}
                  label={g}
                  count={groupCounts[g] || 0}
                  active={g === activeGroup}
                  isAllChip={g === 'All'}
                  accentColor="#FF007F"
                  onSelect={() => setActiveGroup(g)}
                  onRename={(next) => renameGroup(g, next)}
                  onDelete={() => deleteGroup(g)}
                />
              ))}
            </div>

            {/* Sub-category chip row — filters the visible group */}
            <div className="flex gap-1.5 overflow-x-auto pb-2 -mx-1 px-1 mb-4 items-center" data-testid="menu-category-chips">
              <span className="text-[10px] uppercase tracking-[0.3em] text-gray-600 font-['JetBrains_Mono'] pr-1.5 flex-shrink-0">Sub</span>
              {categoryChips.map((cat) => (
                <EditableCategoryChip
                  key={`cat-${cat}`}
                  label={cat}
                  count={categoryCounts[cat] || 0}
                  active={cat === activeCategory}
                  isAllChip={cat === 'All'}
                  accentColor="#00FFFF"
                  sortOrder={categoryOrders[cat] || 0}
                  onSelect={() => setActiveCategory(cat)}
                  onRename={(next) => renameCategory(cat, next)}
                  onDelete={() => deleteCategory(cat)}
                  onSortOrderChange={(nextOrder, label) => setCategoryOrder(nextOrder, label || cat)}
                />
              ))}
            </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {visibleItems.map((item) => (
              <div
                key={item.id}
                className="flex gap-3 p-3 rounded-2xl bg-[#0f0f0f] border border-white/10 hover:border-white/20 transform-gpu"
                data-testid={`menu-item-${item.id}`}
              >
                {item.photo_url ? (
                  <img src={item.photo_url} alt={item.name} className="w-16 h-16 rounded-xl object-cover flex-shrink-0" />
                ) : (
                  <div className="w-16 h-16 rounded-xl bg-white/5 flex items-center justify-center flex-shrink-0">
                    <UtensilsCrossed className="w-6 h-6 text-gray-600" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">{item.category}</p>
                      <div className="flex items-center gap-1.5">
                        <h3 className="text-white font-semibold text-sm truncate font-['Outfit']">{item.name}</h3>
                        {item.is_special && <Sparkles className="w-3 h-3 text-[#FF007F] flex-shrink-0" />}
                        <DietaryTagIcons value={item.dietary_tags} size={11} className="flex-shrink-0" />
                      </div>
                      {item.description && <p className="text-gray-400 text-xs truncate mt-0.5">{item.description}</p>}
                    </div>
                    {(() => {
                      const hasVars = Array.isArray(item.variations) && item.variations.length > 0;
                      if (hasVars) {
                        const prices = item.variations.map((v) => Number(v.price) || 0).filter((p) => p > 0);
                        const min = prices.length ? Math.min(...prices) : 0;
                        const max = prices.length ? Math.max(...prices) : 0;
                        return (
                          <p className="text-[#00FFFF] font-bold text-xs whitespace-nowrap font-mono" title={`${item.variations.length} variants`}>
                            {min === max ? formatCurrency(min) : `${formatCurrency(min)}–${formatCurrency(max)}`}
                          </p>
                        );
                      }
                      return item.price > 0
                        ? <p className="text-[#00FFFF] font-bold text-sm whitespace-nowrap font-mono">{formatCurrency(item.price)}</p>
                        : null;
                    })()}
                  </div>
                  <div className="flex gap-1.5 mt-2">
                    <Button onClick={() => openEdit(item)} variant="ghost" className="h-7 px-2.5 text-[11px] rounded-full text-gray-300 hover:text-white hover:bg-white/5" data-testid={`menu-edit-${item.id}`}>
                      <Pencil className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    <Button onClick={() => remove(item)} variant="ghost" className="h-7 px-2.5 text-[11px] rounded-full text-[#FF3B3B] hover:bg-[#FF3B3B]/10" data-testid={`menu-delete-${item.id}`}>
                      <Trash2 className="w-3 h-3 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          </>
        )}
      </div>

      {/* Edit/Create modal */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={closeEdit}>
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-[#0a0a0a] border border-white/10 rounded-3xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto transform-gpu"
            data-testid="menu-edit-modal"
          >
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-lg font-extrabold text-white font-['Outfit']">
                {editing === 'new' ? 'Add Menu Item' : 'Edit Menu Item'}
              </h2>
              <button onClick={closeEdit} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-400 mb-1 block font-semibold tracking-wider uppercase">Menu Group</label>
                <CategorySelect
                  value={draft.group}
                  onChange={(v) => setDraft({ ...draft, group: v })}
                  options={groupChips.filter((g) => g !== 'All')}
                  testIdPrefix="menu-edit-group"
                />
                <p className="text-[10px] text-gray-600 mt-1">Top-level bucket — e.g. Food, Bar, Desserts. Type to create a new one.</p>
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block font-semibold tracking-wider uppercase">Category</label>
                <CategorySelect
                  value={draft.category}
                  onChange={(v) => setDraft({ ...draft, category: v })}
                  options={existingCategories}
                  testIdPrefix="menu-edit"
                />
                <p className="text-[10px] text-gray-600 mt-1">Section within the group — e.g. Beers, Cocktails, Mains.</p>
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block font-semibold tracking-wider uppercase">Name *</label>
                <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Truffle Burger" className="bg-black/40 border-white/10 text-white h-10 rounded-xl" data-testid="menu-edit-name" />
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block font-semibold tracking-wider uppercase">Description</label>
                <textarea
                  value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  rows={3}
                  className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2 text-white text-sm placeholder:text-gray-600 resize-none focus:outline-none focus:border-[#00FFFF]/40"
                  placeholder="Optional"
                  data-testid="menu-edit-description"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-400 mb-1 block font-semibold tracking-wider uppercase">
                    Base Price (₹)
                    {draft.variations?.length > 0 && (
                      <span className="ml-1 text-[10px] text-gray-600 normal-case">— hidden when variations exist</span>
                    )}
                  </label>
                  <Input type="number" min="0" step="0.01" value={draft.price} onChange={(e) => setDraft({ ...draft, price: parseFloat(e.target.value) || 0 })} className="bg-black/40 border-white/10 text-white h-10 rounded-xl font-mono" data-testid="menu-edit-price" />
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block font-semibold tracking-wider uppercase">Sort</label>
                  <Input type="number" value={draft.sort_order} onChange={(e) => setDraft({ ...draft, sort_order: parseInt(e.target.value, 10) || 0 })} className="bg-black/40 border-white/10 text-white h-10 rounded-xl font-mono" data-testid="menu-edit-sort" />
                </div>
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block font-semibold tracking-wider uppercase">Photo</label>
                <ImageUploader value={draft.photo_url} onChange={(url) => setDraft({ ...draft, photo_url: url })} testIdPrefix="menu-edit" />
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block font-semibold tracking-wider uppercase">Variations</label>
                <VariationsEditor value={draft.variations} onChange={(v) => setDraft({ ...draft, variations: v })} testIdPrefix="menu-edit" />

                <label className="text-xs uppercase text-gray-400 tracking-widest pt-2 block">Dietary / Tags</label>
                <DietaryTagPicker
                  value={draft.dietary_tags || []}
                  onChange={(tags) => setDraft({ ...draft, dietary_tags: tags })}
                  testIdPrefix="menu-edit"
                />
              </div>
              <div className="flex items-center justify-between p-3 rounded-xl bg-black/30 border border-white/5">
                <div>
                  <p className="text-white text-sm font-semibold flex items-center gap-2">
                    Special <Sparkles className="w-3 h-3 text-[#FF007F]" />
                  </p>
                  <p className="text-gray-500 text-[11px]">Pinned to a &quot;Specials&quot; tab on the guest menu.</p>
                </div>
                <Switch
                  checked={draft.is_special}
                  onCheckedChange={(v) => setDraft({ ...draft, is_special: v })}
                  className="data-[state=checked]:bg-[#FF007F]"
                  data-testid="menu-edit-special"
                />
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <Button onClick={closeEdit} variant="ghost" className="flex-1 border border-white/10 rounded-full text-xs font-semibold tracking-wider text-gray-300">CANCEL</Button>
              <Button onClick={save} disabled={saving} className="flex-1 bg-[#00FFFF] hover:bg-[#00d4d4] text-black rounded-full text-xs font-bold tracking-wider" data-testid="menu-edit-save">
                {saving ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
                SAVE
              </Button>
            </div>
          </div>
        </div>
      )}
    </LayoutWrapper>
  );
};

export default MenuAdminPage;
